import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import _QRCodeImport from 'react-qr-code';
// Vite 8 / Rolldown changed CJS default-import interop: the default import can resolve to
// the whole module namespace object { default, QRCode } instead of the component itself.
// Unwrap .default when present so this works under both Rollup (Vite ≤7) and Rolldown (Vite 8+).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const QRCode: typeof _QRCodeImport = (_QRCodeImport as any).default ?? _QRCodeImport;

type UserProfileModel = {
  id?: string;
  publicId?: string;
  name?: string;
  email?: string;
  phone?: string;
  pictureUrl?: string;
  active?: boolean;
  isPublic?: boolean;
  enabled?: boolean;
  owner?: boolean;
  socialMedia?: Array<{
    name?: string;
    socialId?: string;
    url?: string;
  }>;
  createdAt?: unknown;
  updatedAt?: unknown;
};

type EditableFields = {
  name: string;
  email: string;
  phone: string;
};

export default function PublicUserProfile() {
  const { publicId: publicIdParam } = useParams<{ publicId: string }>();
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editFields, setEditFields] = useState<EditableFields>({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [flagSaving, setFlagSaving] = useState(false);
  const [linkSending, setLinkSending] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [sendToMeSending, setSendToMeSending] = useState(false);
  const [sendToMeError, setSendToMeError] = useState<string | null>(null);
  const [sendToMeSuccess, setSendToMeSuccess] = useState<string | null>(null);

  const computeQrSize = () => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 375;
    const max = Math.min(240, Math.floor(w * 0.7));
    return Math.max(140, max);
  };

  const [qrSize, setQrSize] = useState<number>(() => computeQrSize());

  useEffect(() => {
    const onResize = () => setQrSize(computeQrSize());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const normalizePictureUrl = (url?: string, pid?: string): string | undefined => {
    const raw = (url || '').trim();
    if (!raw) return undefined;
    if (raw.includes('/api/user-profiles/profile/')) return raw;

    try {
      const u = new URL(raw, window.location.origin);
      const m = u.pathname.match(
        /^\/(profile|user-profiles)\/([^/]+)\/profile-pic\.(jpg|jpeg|png|webp)$/i
      );
      if (!m) return raw;
      const publicIdFromUrl = m[2];
      const ext = m[3];
      if (pid && publicIdFromUrl !== pid) return raw;
      u.pathname = `/api/user-profiles/profile/${publicIdFromUrl}/profile-pic.${ext}`;
      return u.toString();
    } catch {
      return raw;
    }
  };

  const toDate = value => {
    if (value == null) return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
      const ms = value < 1e12 ? value * 1000 : value;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) {
        const ms = numeric < 1e12 ? numeric * 1000 : numeric;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
      }

      const d = new Date(trimmed);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (typeof value === 'object') {
      if (typeof value.seconds === 'number') {
        const ms =
          value.seconds * 1000 +
          (typeof value.nanos === 'number' ? Math.floor(value.nanos / 1e6) : 0);
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }

    return null;
  };

  const formatDate = value => {
    const d = toDate(value);
    return d ? d.toLocaleDateString() : null;
  };

  const userProfileServiceUrl =
    import.meta.env.VITE_USER_PROFILE_SERVICE_URL === 'RELATIVE'
      ? ''
      : import.meta.env.VITE_USER_PROFILE_SERVICE_URL || '';

  const { data: profile, isLoading: loading, error: profileFetchError } = useQuery<UserProfileModel | null>({
    queryKey: ['publicProfile', publicIdParam, userProfileServiceUrl, currentUser?.uid],
    enabled: Boolean(publicIdParam),
    queryFn: async () => {
      const headers: Record<string, string> = {};
      if (currentUser) {
        const token = await currentUser.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(
        `${userProfileServiceUrl}/api/user-profiles/public/${encodeURIComponent(publicIdParam ?? '')}`,
        { headers }
      );
      if (response.status === 404) throw Object.assign(new Error('Profile not found'), { is404: true });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return response.json() as Promise<UserProfileModel>;
    },
  });

  const { data: canReceive = null } = useQuery<boolean | null>({
    queryKey: ['canReceive', currentUser?.uid, userProfileServiceUrl],
    enabled: Boolean(currentUser),
    queryFn: async () => {
      const token = await currentUser!.getIdToken();
      const response = await fetch(`${userProfileServiceUrl}/api/user-profiles/me/can-receive`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { canReceive?: boolean };
      return Boolean(data?.canReceive);
    },
  });

  const ownerActive = Boolean(profile?.owner) && Boolean(currentUser);
  const { data: messageCount = null, isLoading: messageCountLoading } = useQuery<number | null>({
    queryKey: ['messageCount', currentUser?.uid, userProfileServiceUrl, ownerActive],
    enabled: ownerActive,
    queryFn: async () => {
      const token = await currentUser!.getIdToken();
      const response = await fetch(`${userProfileServiceUrl}/api/user-profiles/me/message-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return null;
      const data = (await response.json()) as { count?: number };
      return typeof data?.count === 'number' ? data.count : null;
    },
  });

  const error = profileFetchError ? (profileFetchError as Error & { is404?: boolean }).message || 'Failed to load profile' : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h1>
              <p className="text-gray-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">No Profile Data</h1>
              <p className="text-gray-600">This profile exists but has no information available.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const effectivePublicId = (profile.publicId || publicIdParam || '').trim();

  const pictureUrl = normalizePictureUrl(profile.pictureUrl, effectivePublicId);
  const avatarSize = 'clamp(96px, 25%, 180px)' as const;
  const isActive = profile.active !== false;
  const profileIsPublic = profile.isPublic !== false;
  const profileEnabled = profile.enabled !== false;

  const isOwner = profile.owner === true;

  const canEdit = isOwner && !profileIsPublic && isActive;

  const profileUrl = effectivePublicId
    ? `${window.location.origin}/profile/${encodeURIComponent(effectivePublicId)}`
    : '';

  const copyText = async (text: string, successMessage: string) => {
    setCopySuccess(null);
    setCopyError(null);

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.top = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('Copy failed');
      }
      setCopySuccess(successMessage);
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyError('Copy failed');
    }
  };

  const handleCopyProfileLink = async () => {
    if (!effectivePublicId) return;
    await copyText(profileUrl, 'Profile link copied');
  };

  const handleCopyPublicId = async () => {
    if (!effectivePublicId) return;
    await copyText(effectivePublicId, 'Public ID copied');
  };

  const handleLinkToMyPublicId = async () => {
    if (!currentUser || !effectivePublicId) return;
    if (isOwner) return;

    setLinkSending(true);
    setLinkError(null);
    setLinkSuccess(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${userProfileServiceUrl}/api/user-profiles/public/${encodeURIComponent(effectivePublicId)}/link`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        setLinkError('You must be logged in to link.');
        return;
      }

      if (response.status === 404) {
        setLinkError('This profile cannot receive links yet.');
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to send link: ${response.status}`);
      }

      const data = (await response.json()) as { messageId?: string };
      setLinkSuccess(data?.messageId ? `Sent (message ${data.messageId})` : 'Sent');
    } catch (err) {
      console.error('Error sending profile link:', err);
      setLinkError(err instanceof Error ? err.message : 'Failed to send link');
    } finally {
      setLinkSending(false);
    }
  };

  const handleSendPublicIdToMe = async () => {
    if (!currentUser || !effectivePublicId) return;
    if (isOwner) return;
    if (!canReceive) return;

    setSendToMeSending(true);
    setSendToMeError(null);
    setSendToMeSuccess(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${userProfileServiceUrl}/api/user-profiles/public/${encodeURIComponent(effectivePublicId)}/send-to-me`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        setSendToMeError('You must be logged in.');
        return;
      }

      if (response.status === 404) {
        setSendToMeError('Unable to send right now.');
        return;
      }

      if (response.status === 400) {
        setSendToMeError('Your account is missing a public ID.');
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to send: ${response.status}`);
      }

      const data = (await response.json()) as { messageId?: string };
      setSendToMeSuccess(data?.messageId ? `Sent (message ${data.messageId})` : 'Sent');
    } catch (err) {
      console.error('Error sending publicId to me:', err);
      setSendToMeError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setSendToMeSending(false);
    }
  };

  const updateFlags = async (next: { isPublic?: boolean; enabled?: boolean }) => {
    if (!currentUser || !effectivePublicId) return;

    setFlagSaving(true);
    setSaveError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${userProfileServiceUrl}/api/user-profiles/public/${encodeURIComponent(effectivePublicId)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            isPublic: typeof next.isPublic === 'boolean' ? next.isPublic : null,
            enabled: typeof next.enabled === 'boolean' ? next.enabled : null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update flags: ${response.status}`);
      }

      const updatedProfile = await response.json();
      queryClient.setQueryData<UserProfileModel | null>(
        ['publicProfile', publicIdParam, userProfileServiceUrl, currentUser?.uid],
        prev => prev ? { ...prev, ...updatedProfile } : updatedProfile
      );
    } catch (err) {
      console.error('Error updating flags:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to update profile flags');
    } finally {
      setFlagSaving(false);
    }
  };

  const handleEditClick = () => {
    setEditFields({
      name: profile.name || '',
      email: profile.email || '',
      phone: profile.phone || '',
    });
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const handleFieldChange = (field: keyof EditableFields, value: string) => {
    setEditFields(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!currentUser || !effectivePublicId) return;

    setSaving(true);
    setSaveError(null);

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${userProfileServiceUrl}/api/user-profiles/public/${encodeURIComponent(effectivePublicId)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: editFields.name || null,
            email: editFields.email || null,
            phone: editFields.phone || null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to save: ${response.status}`);
      }

      const updatedProfile = await response.json();
      queryClient.setQueryData<UserProfileModel | null>(
        ['publicProfile', publicIdParam, userProfileServiceUrl, currentUser?.uid],
        prev => prev ? { ...prev, ...updatedProfile } : updatedProfile
      );
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving profile:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 py-10 flex justify-center"
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        padding: '120px 16px 40px',
      }}
    >
      <div
        className="w-full max-w-3xl px-4 sm:px-6 lg:px-8"
        style={{ width: '100%', maxWidth: 900 }}
      >
        <div
          className="bg-white shadow-lg rounded-2xl overflow-hidden border border-gray-100"
          style={{
            background: '#ffffff',
            color: '#111827',
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid rgba(17,24,39,0.08)',
          }}
        >
          {/* Compact Header Section */}
          <div
            className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-6"
            style={{ padding: 24 }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-5 gap-4">
              {/* Compact Profile Picture / Placeholder */}
              <div className="flex-shrink-0 flex justify-center sm:justify-start">
                {pictureUrl ? (
                  <img
                    className="h-16 w-16 rounded-full border-2 border-white shadow-lg object-cover"
                    src={pictureUrl}
                    alt={profile.name || 'Profile picture'}
                    style={{
                      width: avatarSize,
                      height: avatarSize,
                      objectFit: 'cover',
                      borderRadius: 9999,
                      border: '2px solid #ffffff',
                      display: 'block',
                    }}
                    onError={e => {
                      const img = e.currentTarget as HTMLImageElement;
                      img.style.display = 'none';
                      const sibling = img.nextElementSibling as HTMLElement | null;
                      if (sibling) sibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={`h-16 w-16 rounded-full border-2 border-white shadow-lg bg-gray-300 flex items-center justify-center text-xl font-semibold text-gray-700 ${pictureUrl ? 'hidden' : 'flex'}`}
                  style={{
                    width: avatarSize,
                    height: avatarSize,
                    borderRadius: 9999,
                    border: '2px solid #ffffff',
                    background: '#d1d5db',
                    display: pictureUrl ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'clamp(24px, 6vw, 44px)',
                    fontWeight: 700,
                    color: '#374151',
                  }}
                >
                  {(profile.name && profile.name.trim().charAt(0).toUpperCase()) || '?'}
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white text-center sm:text-left">
                  {profile.name || 'Anonymous User'}
                </h1>
                <p
                  className="text-slate-200 mt-1 text-base text-center sm:text-left break-all"
                  style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                >
                  Public ID: {effectivePublicId}
                </p>

                {!isOwner && (
                  <div
                    className="mt-3 flex flex-col sm:flex-row sm:items-center sm:gap-2"
                    style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}
                  >
                    <button
                      onClick={handleLinkToMyPublicId}
                      disabled={!currentUser || linkSending}
                      className="inline-flex items-center px-3 py-2 rounded-md text-white"
                      style={{
                        background: '#0f766e',
                        fontSize: 14,
                        fontWeight: 700,
                        opacity: !currentUser || linkSending ? 0.7 : 1,
                      }}
                    >
                      {!currentUser ? 'Log in to link' : linkSending ? 'Linking…' : 'Link to my Public ID'}
                    </button>

                    <button
                      onClick={handleSendPublicIdToMe}
                      disabled={!currentUser || canReceive !== true || sendToMeSending}
                      className="inline-flex items-center px-3 py-2 rounded-md text-white"
                      style={{
                        background: '#155e75',
                        fontSize: 14,
                        fontWeight: 700,
                        opacity: !currentUser || canReceive !== true || sendToMeSending ? 0.7 : 1,
                      }}
                    >
                      {!currentUser ? 'Log in to send' : sendToMeSending ? 'Sending…' : 'Send Public ID to Me'}
                    </button>

                    {linkSuccess && (
                      <div className="text-sm" style={{ color: '#e2e8f0' }}>
                        {linkSuccess}
                      </div>
                    )}
                    {linkError && (
                      <div className="text-sm" style={{ color: '#fecaca' }}>
                        {linkError}
                      </div>
                    )}

                    {sendToMeSuccess && (
                      <div className="text-sm" style={{ color: '#e2e8f0' }}>
                        {sendToMeSuccess}
                      </div>
                    )}
                    {sendToMeError && (
                      <div className="text-sm" style={{ color: '#fecaca' }}>
                        {sendToMeError}
                      </div>
                    )}

                    {!currentUser && !linkError && !linkSuccess && (
                      <div className="text-sm" style={{ color: '#e2e8f0' }}>
                        <a href="/login" className="underline">
                          Log in
                        </a>{' '}
                        to link or send this Public ID.
                      </div>
                    )}

                    {currentUser && canReceive === false && !sendToMeError && !sendToMeSuccess && (
                      <div className="text-sm" style={{ color: '#e2e8f0' }}>
                        To enable “Send Public ID to Me”, publish your Public ID and Public Key.
                      </div>
                    )}

                    {currentUser && canReceive === null && (
                      <div className="text-sm" style={{ color: '#e2e8f0' }}>
                        Checking message receive status…
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {isOwner && (
            <div
              className="px-6 py-4"
              style={{ padding: 16, background: '#ecfdf5', borderBottom: '1px solid rgba(17,24,39,0.08)' }}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm" style={{ color: '#065f46', fontWeight: 700 }}>
                    This is you
                  </div>
                  <div className="text-sm" style={{ color: '#065f46', marginTop: 4 }}>
                    Visibility:
                    {' '}
                    {profileIsPublic ? 'Public' : 'Private'}
                    {' '}| Draft:
                    {' '}
                    {profileEnabled ? 'Enabled' : 'Disabled'}
                  </div>
                </div>

                <div className="flex gap-2" style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={handleCopyProfileLink}
                    className="inline-flex items-center px-3 py-2 rounded-md text-white"
                    style={{ background: '#0f766e', fontSize: 14, fontWeight: 700 }}
                  >
                    Copy Link
                  </button>
                  <button
                    onClick={handleCopyPublicId}
                    className="inline-flex items-center px-3 py-2 rounded-md text-white"
                    style={{ background: '#155e75', fontSize: 14, fontWeight: 700 }}
                  >
                    Copy Public ID
                  </button>
                  <button
                    onClick={() => updateFlags({ isPublic: !profileIsPublic })}
                    disabled={flagSaving}
                    className="inline-flex items-center px-3 py-2 rounded-md text-white"
                    style={{ background: '#1d4ed8', fontSize: 14, fontWeight: 700, opacity: flagSaving ? 0.7 : 1 }}
                  >
                    {profileIsPublic ? 'Set Private' : 'Set Public'}
                  </button>
                  <button
                    onClick={() => updateFlags({ enabled: !profileEnabled })}
                    disabled={flagSaving}
                    className="inline-flex items-center px-3 py-2 rounded-md text-white"
                    style={{ background: '#0f766e', fontSize: 14, fontWeight: 700, opacity: flagSaving ? 0.7 : 1 }}
                  >
                    {profileEnabled ? 'Disable Draft (Hide)' : 'Enable Draft'}
                  </button>
                </div>
              </div>
              {(copySuccess || copyError) && (
                <div className="text-sm" style={{ marginTop: 8, color: copyError ? '#b91c1c' : '#065f46' }}>
                  {copyError || copySuccess}
                </div>
              )}
              {saveError && (
                <div className="text-sm" style={{ color: '#b91c1c', marginTop: 8 }}>
                  {saveError}
                </div>
              )}
            </div>
          )}

          {isOwner && (
            <div className="px-6 py-6" style={{ padding: 24, background: '#f8fafc', borderBottom: '1px solid rgba(17,24,39,0.08)' }}>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}
              >
                <div
                  className="rounded-xl border border-gray-100 bg-white p-4"
                  style={{ borderRadius: 12, border: '1px solid rgba(17,24,39,0.08)', background: '#ffffff', padding: 16 }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Public ID
                  </div>
                  <div style={{ marginTop: 8, fontSize: 16, fontWeight: 700, color: '#0f172a', wordBreak: 'break-all' }}>
                    {effectivePublicId || '—'}
                  </div>
                </div>

                <div
                  className="rounded-xl border border-gray-100 bg-white p-4"
                  style={{ borderRadius: 12, border: '1px solid rgba(17,24,39,0.08)', background: '#ffffff', padding: 16 }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Visibility
                  </div>
                  <div style={{ marginTop: 8, fontSize: 16, fontWeight: 700, color: profileIsPublic ? '#0f766e' : '#b91c1c' }}>
                    {profileIsPublic ? 'Public' : 'Private'}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
                    Draft: {profileEnabled ? 'Enabled' : 'Disabled'}
                  </div>
                </div>

                <div
                  className="rounded-xl border border-gray-100 bg-white p-4"
                  style={{ borderRadius: 12, border: '1px solid rgba(17,24,39,0.08)', background: '#ffffff', padding: 16 }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Messages
                  </div>
                  <div style={{ marginTop: 8, fontSize: 28, fontWeight: 700, color: messageCount != null && messageCount > 0 ? '#16a34a' : '#0f172a' }}>
                    {messageCountLoading ? '…' : messageCount ?? '—'}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
                    {messageCountLoading
                      ? 'Checking…'
                      : messageCount === 1
                        ? 'message waiting'
                        : 'messages waiting'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isActive && (
            <div
              className="px-6 py-4"
              style={{ padding: 16, background: '#fff7ed', borderBottom: '1px solid rgba(17,24,39,0.08)' }}
            >
              <div className="text-sm" style={{ color: '#9a3412', fontWeight: 600 }}>
                This MPD ID page is inactive.
              </div>
              <div className="text-sm" style={{ color: '#9a3412', marginTop: 4 }}>
                {currentUser
                  ? 'To activate it, purchase an MPD ID Page subscription.'
                  : 'To activate it, log in and purchase an MPD ID Page subscription.'}
              </div>
              <div style={{ marginTop: 10 }}>
                <a
                  href={currentUser ? '/buy' : '/login'}
                  className="inline-flex items-center px-3 py-2 rounded-md text-white"
                  style={{ background: '#1d4ed8', fontSize: 14, fontWeight: 700 }}
                >
                  {currentUser ? 'Activate' : 'Log in to Activate'}
                </a>
              </div>
            </div>
          )}

          {!profileIsPublic && isActive && (
            <div
              className="px-6 py-4"
              style={{ padding: 16, background: '#eff6ff', borderBottom: '1px solid rgba(17,24,39,0.08)' }}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm" style={{ color: '#1e40af', fontWeight: 600 }}>
                    This profile is set to private.
                  </div>
                  <div className="text-sm" style={{ color: '#1e40af', marginTop: 4 }}>
                    Contact information is hidden from public view. You can edit the displayed fields below.
                  </div>
                </div>
                {canEdit && !isEditing && (
                  <button
                    onClick={handleEditClick}
                    className="inline-flex items-center px-3 py-2 rounded-md text-white"
                    style={{ background: '#1d4ed8', fontSize: 14, fontWeight: 700 }}
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Compact Content Section */}
          <div className="px-6 py-6" style={{ padding: 24 }}>
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24, width: '100%', minWidth: 0 }}
            >
              {/* QR Code */}
              {profileUrl && (
                <div
                  className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm"
                  style={{
                    background: '#ffffff',
                    borderRadius: 12,
                    padding: 20,
                    border: '1px solid rgba(17,24,39,0.08)',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        background: '#ffffff',
                        padding: 10,
                        borderRadius: 12,
                        border: '1px solid rgba(17,24,39,0.08)',
                        width: qrSize,
                        height: qrSize,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                      }}
                    >
                      <QRCode value={profileUrl} style={{ width: '100%', height: '100%' }} />
                    </div>

                    <div
                      className="text-sm text-gray-700 break-all"
                      style={{ textAlign: 'center', wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                    >
                      {profileUrl}
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div
                className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm"
                style={{
                  background: '#ffffff',
                  borderRadius: 12,
                  padding: 20,
                  border: '1px solid rgba(17,24,39,0.08)',
                  minWidth: 0,
                }}
              >
                <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <svg
                    width="24"
                    height="24"
                    className="h-6 w-6 text-gray-600 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  Contact Information
                </h2>

                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={editFields.name}
                        onChange={e => handleFieldChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={editFields.email}
                        onChange={e => handleFieldChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }}
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={editFields.phone}
                        onChange={e => handleFieldChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    {saveError && (
                      <div className="text-red-600 text-sm" style={{ color: '#dc2626' }}>
                        {saveError}
                      </div>
                    )}
                    <div className="flex gap-2" style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 rounded-md text-white"
                        style={{ background: '#1d4ed8', padding: '8px 16px', borderRadius: 6, fontWeight: 600, opacity: saving ? 0.7 : 1 }}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={saving}
                        className="px-4 py-2 rounded-md"
                        style={{ background: '#e5e7eb', padding: '8px 16px', borderRadius: 6, fontWeight: 600 }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {isActive && profileIsPublic && profile.email && (
                      <div className="flex items-center">
                        <svg
                          width="20"
                          height="20"
                          className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                        <a
                          href={`mailto:${profile.email}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                        >
                          {profile.email}
                        </a>
                      </div>
                    )}

                    {isActive && profileIsPublic && profile.phone && (
                      <div className="flex items-center">
                        <svg
                          width="20"
                          height="20"
                          className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                        </svg>
                        <a
                          href={`tel:${profile.phone}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {profile.phone}
                        </a>
                      </div>
                    )}

                    {!isActive && (
                      <p className="text-gray-500 italic">
                        Contact information is hidden while this profile is inactive.
                      </p>
                    )}

                    {isActive && !profileIsPublic && (
                      <p className="text-gray-500 italic">
                        Contact information is hidden (profile is private).
                      </p>
                    )}

                    {isActive && profileIsPublic && !profile.email && !profile.phone && (
                      <p className="text-gray-500 italic">No contact information available</p>
                    )}
                  </div>
                )}
              </div>

              {/* Social Media */}
              <div
                className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm"
                style={{
                  background: '#ffffff',
                  borderRadius: 12,
                  padding: 20,
                  border: '1px solid rgba(17,24,39,0.08)',
                  minWidth: 0,
                }}
              >
                <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <svg
                    width="24"
                    height="24"
                    className="h-6 w-6 text-gray-600 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Social Media
                </h2>

                {(() => {
                  const rows = (profile.socialMedia || []).filter(sm => {
                    const socialId = (sm?.socialId || '').trim();
                    const url = (sm?.url || '').trim();
                    return socialId.length > 0 || url.length > 0;
                  });

                  if (rows.length === 0) {
                    return <p className="text-gray-500 italic">No social media profiles available</p>;
                  }

                  return (
                    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                      <table
                        style={{
                          width: '100%',
                          minWidth: 520,
                          borderCollapse: 'collapse',
                        }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: 'left',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#4b5563',
                                padding: '8px 0',
                                borderBottom: '1px solid rgba(17,24,39,0.12)',
                              }}
                            >
                              Platform
                            </th>
                            <th
                              style={{
                                textAlign: 'left',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#4b5563',
                                padding: '8px 0',
                                borderBottom: '1px solid rgba(17,24,39,0.12)',
                              }}
                            >
                              Social ID
                            </th>
                            <th
                              style={{
                                textAlign: 'left',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#4b5563',
                                padding: '8px 0',
                                borderBottom: '1px solid rgba(17,24,39,0.12)',
                              }}
                            >
                              URL
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {rows.map((sm, idx) => {
                            const label = (sm?.name || '').trim() || `Social ${idx + 1}`;
                            const socialId = (sm?.socialId || '').trim();
                            const url = (sm?.url || '').trim();

                            return (
                              <tr key={`${label}-${idx}`}>
                                <td
                                  style={{
                                    padding: '10px 0',
                                    borderBottom: '1px solid rgba(17,24,39,0.06)',
                                    color: '#111827',
                                    fontWeight: 600,
                                    verticalAlign: 'top',
                                  }}
                                >
                                  <span style={{ wordBreak: 'break-word' }}>{label}</span>
                                </td>
                                <td
                                  style={{
                                    padding: '10px 0',
                                    borderBottom: '1px solid rgba(17,24,39,0.06)',
                                    color: '#374151',
                                    verticalAlign: 'top',
                                  }}
                                >
                                  <span style={{ wordBreak: 'break-all' }}>{socialId || '-'}</span>
                                </td>
                                <td
                                  style={{
                                    padding: '10px 0',
                                    borderBottom: '1px solid rgba(17,24,39,0.06)',
                                    verticalAlign: 'top',
                                  }}
                                >
                                  {url ? (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={url}
                                      style={{
                                        color: '#2563eb',
                                        textDecoration: 'underline',
                                        wordBreak: 'break-all',
                                      }}
                                    >
                                      {url}
                                    </a>
                                  ) : (
                                    <span style={{ color: '#374151' }}>-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Profile Metadata - More Compact */}
            {(formatDate(profile.createdAt) || formatDate(profile.updatedAt)) && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="text-sm text-gray-500 flex flex-col sm:flex-row sm:gap-6 gap-1">
                  {formatDate(profile.createdAt) && (
                    <p>Profile created: {formatDate(profile.createdAt)}</p>
                  )}
                  {formatDate(profile.updatedAt) && (
                    <p>Last updated: {formatDate(profile.updatedAt)}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
